import React, { useCallback, useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { AccommodationsSectionLayoutType } from '@shared/types/Accommodations.types';

// In-memory override for the accommodations layout, set by PreviewListener when the
// admin postMessages a new selection. Stays in app state — never written to Apollo cache —
// so we don't pollute BE-truth with preview-only values. In production guest viewing,
// no admin sends messages and the override stays `null`, falling through to the cached
// saved layout.
type AccommodationsLayoutOverrideStore = Readonly<{
  layoutOverride: AccommodationsSectionLayoutType | null;
  setLayoutOverride: (layout: AccommodationsSectionLayoutType | null) => void;
}>;

const noop = () => {};

const [Provider, useStore] = createContext<AccommodationsLayoutOverrideStore>({
  name: 'AccommodationsLayoutOverrideContext',
  // No-op default so consumers (like the Accommodations widget) work even when the
  // provider isn't mounted — important for any guest render path that bypasses GuestSiteBody.
  strict: false,
  initialValue: { layoutOverride: null, setLayoutOverride: noop },
});

export const useAccommodationsLayoutOverride = (): AccommodationsLayoutOverrideStore => useStore(false) ?? { layoutOverride: null, setLayoutOverride: noop };

export const AccommodationsLayoutOverrideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [layoutOverride, setLayoutOverride] = useState<AccommodationsSectionLayoutType | null>(null);
  const setOverride = useCallback((layout: AccommodationsSectionLayoutType | null) => {
    setLayoutOverride(layout);
  }, []);
  const value = useMemo(() => ({ layoutOverride, setLayoutOverride: setOverride }), [layoutOverride, setOverride]);
  return <Provider value={value}>{children}</Provider>;
};
